import {
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { CreditScoreOutlined, Search } from '@mui/icons-material';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { getColor } from '../../../../../assets/theme';
import InputFieldsController from '../../../../../components/form/controller/input-fields.controller';
import { creditMemoUsedSchema } from '../../../interface/room-posting-schema-interface';

import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { usePostCreditMemoMutation } from '../../../../../api/front-office/roomposting.api.rtk';
import LoadingComponent from '../../../../../components/animation/loading-component';
import CustomNumberFormat from '../../../../../components/form/custom-number-format';
import useInformationDialog from '../../../../../hooks/useInformationDialog';
import useToast from '../../../../../hooks/useToast';
import { RootState, useAppSelector } from '../../../../../store';
import {
  RoomPostingCreditMemoIE,
  RoomPostingCreditMemoVoucherIE,
} from '../../../interface/room-posting-interface';
import FoRoompostingCreditmemoTable from './fo-roomposting-creditmemo-table';
import { selectFOSalesDate } from '@store/selector/app.selector';
export interface FormValues {
  cm_slip_no: string;
  total_credit: number;
  credit_avail: number;
  amount_used: number;
  cmRemarks: string;
}

const FORoomPostingCreditMemoUsed = ({
  handleClose,
  selectedFolioUuid,
  selectedSubGroup,
  ledgerRefetch,
}: {
  handleClose: () => void;
  selectedFolioUuid: string;
  selectedSubGroup: number;
  ledgerRefetch: () => void;
}) => {
  const { primary, white } = getColor();
  const { toast } = useToast();
  const currentSalesDate = useAppSelector(selectFOSalesDate);

  const { DialogInformation, onShowInfoDialog } = useInformationDialog();

  const [selCMDetails, setSelCMDetails] =
    useState<RoomPostingCreditMemoVoucherIE>();
  const selectedPropertyID = useSelector(
    (state: RootState) => state.properties.selectedProperty.value,
  );

  const methods = useForm<FormValues>({
    defaultValues: {
      cm_slip_no: '',
      total_credit: 0,
      credit_avail: 0,
      amount_used: 0,
      cmRemarks: '',
    },
    resolver: yupResolver(creditMemoUsedSchema),
    mode: 'onChange',
  });

  const {
    control,
    formState: { errors },
    handleSubmit,
    setValue,
    watch,
  } = methods;

  const [isOpenCM, setIsOpenCM] = useState(false);

  const handleOpenCMList = () => {
    setIsOpenCM(true);
  };

  const handleCloseCMList = () => {
    setIsOpenCM(false);
  };

  const [postCMTrans, { isLoading: isFetching }] = usePostCreditMemoMutation();

  const onSubmit: SubmitHandler<FormValues> = async (data: FormValues) => {
    const transactionIdentifier = selectedFolioUuid;
    const subGroupId = selectedSubGroup;
    const cutOffDate = moment(currentSalesDate).format('YYYY-MM-DD');

    if (!selCMDetails || selCMDetails?.id === 0) {
      onShowInfoDialog({
        title: 'No selected Credit Memo',
        desc: 'Please select Credit Memo Transactions on the list.',
      });
      return;
    }

    // const selectedEntry = entries.find((entry) => entry.remarks === 'CMP');
    // get CMP ID
    // if (!selectedEntry || selectedEntry?.id === 0) {
    //   onShowInfoDialog({
    //     title: 'No default Credit Memo Payment Entry',
    //     desc: 'Please go to Entries Management and update the default Credit Memo Payment entry.',
    //   });
    //   return;
    // }

    if (data.amount_used > selCMDetails?.creditBal) {
      onShowInfoDialog({
        title: 'Insufficient Credit Memo',
        desc: 'Please enter a Credit Memo amount that is less than or equal to the available credit.',
      });
      return;
    }

    const postCreditMemo: RoomPostingCreditMemoIE = {
      properties: [
        {
          property: {
            propertyID: selectedPropertyID,
            cm_transaction: [
              {
                transaction_type: 'USED',
                transaction_identifier: transactionIdentifier,
                cutoff_date: cutOffDate,
                module_id: 1,
                sub_group_id: subGroupId,
                remarks: data.cmRemarks,
                guest_id: selCMDetails?.guestID || 0,
                guest_name: selCMDetails?.guestName || 'N/A',
                credit_amount: data.amount_used,
                cm_id: selCMDetails?.id || 0,
              },
            ],
          },
        },
      ],
    };

    await postCMTrans(postCreditMemo)
      .unwrap()
      .then((cm_result) => {
        if (cm_result?.data?.[0]?.result) {
          toast({
            severity: 'success',
            message: 'Successfully posted Credit Memo Payment.',
            duration: 1200,
          });
          ledgerRefetch();
          handleClose();
        } else {
          toast({
            severity: 'error',
            message: `Insufficient credit memo balance.`,
            duration: 1200,
          });
          setValue('cm_slip_no', '');
          setValue('total_credit', 0);
          setValue('credit_avail', 0);
          setValue('amount_used', 0);
        }
      })
      .catch((error) =>
        toast({
          severity: 'error',
          message: `Error occurred apply credit memo: ${error}`,
        }),
      );
  };

  return (
    <FormProvider {...methods}>
      <Stack sx={{ minWidth: '370px' }}>
        {isFetching && <LoadingComponent />}
        <Stack component='form' onSubmit={handleSubmit(onSubmit)}>
          <Stack
            sx={{ p: 3, borderBottom: '1px dashed lightgray' }}
            gap='5px'
            width='100%'
          >
            <FormControl sx={{ width: '100%' }} className='form-control'>
              <Typography sx={{ flexGrow: 1, mb: '10px' }}>
                Select Credit Memo
              </Typography>
              <InputFieldsController
                label='Credit Memo No.'
                name='cm_slip_no'
                control={control}
                errors={errors}
                inputStyle={{
                  '.MuiInputBase-root': { width: '100%' },
                }}
                InputProps={{
                  readOnly: true,
                  startAdornment: (
                    <InputAdornment position='start'>
                      <CreditScoreOutlined />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        edge='end'
                        onClick={(e) => {
                          e.preventDefault();
                          handleOpenCMList();
                        }}
                        style={{
                          padding: '6px',
                          backgroundColor: primary,
                          borderRadius: '4px',
                          color: white,
                        }}
                      >
                        <Search />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder='CM000000000000#'
                borderErrorOnly
              />

              <InputFieldsController
                label='Total Credit Amount'
                name='total_credit'
                control={control}
                errors={errors}
                wrapperStyle={{
                  minWidth: '150px',
                  width: '100%',
                }}
                placeholder='0.00'
                borderErrorOnly
                InputProps={{
                  readOnly: true,
                  inputComponent: CustomNumberFormat,
                }}
              />

              <InputFieldsController
                label='Total Credit Available'
                name='credit_avail'
                control={control}
                errors={errors}
                inputProps={{
                  maxLength: 8,
                  style: { textAlign: 'right' },
                }}
                placeholder='0.00'
                InputProps={{
                  readOnly: true,
                  inputComponent: CustomNumberFormat,
                }}
              />

              <InputFieldsController
                label='Amount Used'
                name='amount_used'
                control={control}
                errors={errors}
                inputProps={{
                  maxLength: 8,
                  style: { textAlign: 'right' },
                }}
                placeholder='0.00'
                InputProps={{
                  inputComponent: CustomNumberFormat,
                }}
              />

              <InputFieldsController
                label='Remarks'
                name='cmRemarks'
                placeholder='Enter credit memo remarks'
                control={control}
                errors={errors}
                multiline
                rows={4}
                helperText={`${watch('cmRemarks')?.length || 0}/100`}
                wrapperStyle={{ mb: 0 }}
              />
            </FormControl>
          </Stack>

          {/* BUTTON */}
          <Stack alignItems='flex-end' p={3}>
            <Stack direction='row' gap='5px'>
              <Button
                sx={{
                  width: '75px',
                  height: '35px',
                  border: '1px solid',
                  backgroundColor: 'transparent',
                }}
                onClick={handleClose}
              >
                Cancel
              </Button>
              <LoadingButton
                type='submit'
                onClick={handleSubmit(onSubmit)}
                variant='contained'
                sx={{ height: '35px', width: '80px' }}
                loading={isFetching}
              >
                Post
              </LoadingButton>
            </Stack>
          </Stack>
        </Stack>
        <DialogInformation />
      </Stack>

      <FoRoompostingCreditmemoTable
        isOpen={isOpenCM}
        onClose={handleCloseCMList}
        selectedCM={(cmDetails: RoomPostingCreditMemoVoucherIE) => {
          setValue('cm_slip_no', cmDetails?.transNo);
          setValue('total_credit', cmDetails?.creditAmount);
          setValue('credit_avail', cmDetails?.creditBal);
          setValue('amount_used', cmDetails?.creditBal);
          setValue('cmRemarks', '');
          setSelCMDetails(cmDetails);
        }}
      />
    </FormProvider>
  );
};

export default FORoomPostingCreditMemoUsed;
